import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  MenuItem,
  MenuItemProps,
} from "@chakra-ui/react";
import React from "react";
import { IoLockOpen } from "react-icons/io5";

import { Button, useToast } from "../../../components";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { useUnkeepCallMutation } from "../../graphql";

interface UnkeepCallButtonProps extends Omit<MenuItemProps, "onClick"> {
  call: { id: string };
}

const UnkeepCallButton: React.FC<UnkeepCallButtonProps> = ({
  call,
  ...menuItemProps
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const sendGAEvent = useSendGAEvent();

  const cancelRef = React.useRef<HTMLButtonElement | null>(null);
  const toast = useToast();

  const [keepCalls, { loading }] = useUnkeepCallMutation({
    onCompleted: () => {
      toast({
        title: "Success",
        status: "success",
      });
      setIsModalOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Operation failed. Please try again later.",
        status: "error",
      });
    },
  });

  return (
    <>
      <MenuItem
        data-testid="call-settings-menu-unkeep"
        color="red.600"
        icon={<IoLockOpen />}
        isDisabled={loading}
        onClick={() => {
          setIsModalOpen(true);
        }}
        {...menuItemProps}
      >
        Allow Deletion
      </MenuItem>
      <AlertDialog
        isOpen={isModalOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsModalOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Allow deletion of this interview?
            </AlertDialogHeader>
            <AlertDialogBody>
              This interview will now show up in your organization&apos;s data
              retention section at the scheduled time.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                variant="outline"
                size="sm"
                ref={cancelRef}
                isDisabled={loading}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                data-testid="call-delete-confirm-button"
                isLoading={loading}
                onClick={() => {
                  sendGAEvent("delete", "call_review");
                  keepCalls({
                    variables: { id: call.id },
                  });
                }}
                ml={3}
              >
                Allow deletion
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

UnkeepCallButton.displayName = "UnkeepCallButton";

export default UnkeepCallButton;
